import React, { useState } from "react";
import { Helmet } from "react-helmet";
import styled from "styled-components";
import EmbeddedClinicList from "../../components/EmbeddedClinicList";
import {
  TwoColRow,
  ColText,
  ColImage,
  ColImg,
  SectionBottomImage,
} from "../../components/ContentLayouts";

// ── Image imports ───────────────────────────────────────────────────────────────
import imgBtDetect from "../../assets/images/content/pet-blood-test/bt-detect.png";
import imgBtLocate from "../../assets/images/content/pet-blood-test/bt-locate.png";

const faqs = [
  {
    question: "Why are blood and urine tests important for pets?",
    answer:
      "They detect health issues early, often before symptoms appear, so treatment can start sooner.",
  },
  {
    question: "What diseases can a pet blood test detect?",
    answer:
      "It can help detect infections, diabetes, anaemia, organ dysfunction and thyroid issues, among other conditions.",
  },
  {
    question: "What can a urine test reveal about my pet's health?",
    answer:
      "It can show signs of urinary infections, kidney problems, dehydration, and abnormal protein or sugar levels.",
  },
  {
    question: "How often should pets have routine laboratory testing?",
    answer:
      "Most healthy adult pets benefit from annual testing; senior pets or those with existing conditions may need it more often.",
  },
  {
    question: "How quickly are test results available?",
    answer:
      "This depends on the clinic and test type. Basic panels are often ready within a day; detailed profiles may take longer.",
  },
  {
    question: "How much does a pet blood test cost?",
    answer:
      "Pet blood test cost varies by clinic and panel selected. Check exact pricing with the clinic you choose through PetQura.",
  },
  {
    question: "Do I need to prepare my pet before a blood test?",
    answer:
      "Some tests require fasting or specific preparation. The clinic you book through PetQura will guide you beforehand.",
  },
  {
    question: "How does PetQura help me book a pet blood test near me?",
    answer:
      "Search by your location and the service you need. We show you nearby clinics offering blood and urine tests, so you can compare and book easily.",
  },
];

const FaqItem = ({ question, answer }) => {
  const [open, setOpen] = useState(false);
  return (
    <FaqRow>
      <FaqQuestion onClick={() => setOpen(!open)}>
        <span>{question}</span>
        <FaqIcon>{open ? "−" : "+"}</FaqIcon>
      </FaqQuestion>
      {open && <FaqAnswer>{answer}</FaqAnswer>}
    </FaqRow>
  );
};

const PetBloodTest = () => {
  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Pet Blood and Urine Tests in Bangalore | PetQura</title>
        <meta
          name="description"
          content="Book a pet blood test in Bangalore through PetQura. Find certified veterinary labs and clinics offering diagnostic tests for dogs, cats and all pets near you."
        />
        <link
          rel="canonical"
          href="https://petqura.com/services/pet-blood-test-bangalore"
        />
      </Helmet>

      <PageWrapper>
        <HeroSection>
          <h1>Pet Blood and Urine Tests in Bangalore by PetQura</h1>
          <EmbeddedClinicList serviceId={null} previewMode />
          <p>
            As a pet parent, you notice the small things - a slower walk, a
            lighter appetite, a little extra sleep. A{" "}
            <em>pet blood test</em> often catches what these small changes are
            trying to tell you, long before symptoms turn serious. Routine and
            diagnostic blood and urine tests give your vet a clear picture of
            how your pet's organs, immune system and metabolism are working,
            even when your dog or cat seems perfectly fine.
          </p>
          <p>
            At PetQura, we make it simple to find a{" "}
            <em>pet blood test near you</em> in Bangalore. Instead of calling
            around or guessing which clinic offers the right diagnostics, you
            search by service and location, and we show you veterinary clinics
            near you that can help. Booking becomes quicker, and both you and
            your pet feel a little less stressed about the process.
          </p>
        </HeroSection>

        {/* ── What a Blood Test Can Detect ── */}
        <ContentSection>
          <SectionHeading>
            What a Cat or Dog Blood Test Can Detect
          </SectionHeading>
          <p>
            A cat or dog blood test can reveal a lot that isn't visible during
            a regular check-up. Vets use it to look closely at your pet's
            internal health and catch problems early, when they're easier to
            manage:
          </p>
          <BulletList>
            <li>
              Organ function – how well the liver and kidneys are working
            </li>
            <li>
              Infections – bacterial, viral or parasitic infections in the
              body
            </li>
            <li>
              Diabetes and blood sugar issues – through glucose level readings
            </li>
            <li>
              Anaemia or blood disorders – through red and white blood cell
              counts
            </li>
            <li>Thyroid problems – especially common in older cats and dogs</li>
            <li>
              Overall wellness – a general snapshot of health before surgery,
              vaccination or travel
            </li>
          </BulletList>
          <p>
            A urine test adds another layer of insight, especially for kidney
            health, urinary infections and hydration levels. Together, both
            tests give your vet the fuller picture needed to guide the right
            care.
          </p>
          <SectionBottomImage
            src={imgBtDetect}
            alt="What a cat or dog blood test can detect"
          />
        </ContentSection>

        {/* ── What to Expect ── */}
        <ContentSection>
          <SectionHeading>
            What to Expect During a Blood or Urine Test
          </SectionHeading>
          <p>
            We understand that any mention of a "test" can make pet parents,
            and pets, a little anxious. In reality, the process is quick and
            fairly routine. Once you've used PetQura to find and book a clinic
            near you, the vet team will explain the procedure, gently settle
            your pet, and draw a small blood sample or collect a urine sample.
            Most pets manage this well, especially with a calm handler and a
            familiar owner close by. The clinic will also let you know how
            long results will take.
          </p>
        </ContentSection>

        {/* ── Cost ── */}
        <ContentSection>
          <SectionHeading>Cost of a Blood or Urine Test</SectionHeading>
          <p>
            Pet blood test cost in Bangalore isn't fixed. It depends on a few
            factors. The type of panel matters most: a basic complete blood
            count costs less than a full profile covering liver, kidney,
            thyroid and glucose readings together. The clinic's location, the
            equipment used, and whether samples are processed in-house or sent
            to an external lab can also affect cat or dog blood test cost.
            Urine tests are usually priced separately or bundled with blood
            work as part of a wellness package.
          </p>
          <p>
            Since every clinic sets its own pricing, we recommend checking
            directly with the clinic you book through PetQura, so you know
            what to expect before your appointment.
          </p>
        </ContentSection>

        {/* ── How PetQura Helps ── */}
        <ContentSection>
          <SectionHeading>How PetQura Helps You Find the Right Clinic</SectionHeading>
          <TwoColRow>
            <ColText>
              <p>
                Searching for a reliable <em>pet blood test near you</em>{" "}
                shouldn't take up your afternoon. With PetQura, you share your
                location and the service you need — a blood or urine test —
                and we show veterinary clinics near you that offer it. You
                can compare options, check their location on the map, and
                pick an appointment slot that works for you.
              </p>
              <p>
                This saves you the back-and-forth of phone calls and lets you
                plan around your pet's routine, instead of the other way
                around. Whether you're a first-time pet parent or you've been
                through this before, having everything in one place makes the
                experience calmer, for you and for your pet.
              </p>
            </ColText>
            <ColImage>
              <ColImg
                src={imgBtLocate}
                alt="How PetQura helps you find the right clinic for a pet blood test"
              />
            </ColImage>
          </TwoColRow>
        </ContentSection>

        {/* ── Choosing the Right Test ── */}
        <ContentSection>
          <SectionHeading>Choosing the Right Test for Your Pet</SectionHeading>
          <p>
            Not every pet needs the same panel. Your vet usually recommends
            tests based on your pet's age, breed, symptoms, or an upcoming
            procedure. Common types you may come across while booking through
            PetQura include:
          </p>
          <BulletList>
            <li>
              Complete Blood Count (CBC) – checks red cells, white cells and
              platelets
            </li>
            <li>Liver Function Test (LFT) – assesses liver health</li>
            <li>Kidney Function Test (KFT) – checks kidney performance</li>
            <li>
              Urinalysis – examines urine for infection, crystals or
              abnormal proteins
            </li>
            <li>
              Wellness panels – a combined set of tests for annual check-ups
            </li>
          </BulletList>
          <p>
            If you're unsure which test your pet needs, the clinic you book
            through PetQura can guide you.
          </p>
        </ContentSection>

        {/* ── FAQ ── */}
        <ContentSection>
          <SectionHeading>Frequently Asked Questions (FAQs)</SectionHeading>
          <FaqList>
            {faqs.map((faq, idx) => (
              <FaqItem key={idx} question={faq.question} answer={faq.answer} />
            ))}
          </FaqList>
        </ContentSection>
      </PageWrapper>
    </>
  );
};

export default PetBloodTest;

// ─── Styled Components ────────────────────────────────────────────────────────

const PageWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 3rem 5% 5rem;

  @media (max-width: 768px) {
    padding: 2rem 5% 4rem;
  }
`;

const HeroSection = styled.section`
  margin-bottom: 3rem;

  h1 {
    font-size: 2rem;
    font-weight: 700;
    color: #1a1a2e;
    margin-bottom: 1.5rem;
    font-family: Montserrat, sans-serif;
    text-align: center;

    @media (max-width: 768px) {
      font-size: 1.5rem;
    }
  }

  p {
    font-size: 16px;
    line-height: 1.8;
    color: #3a3a3a;
    font-family: Montserrat, sans-serif;
    margin-top: 1.5rem;
    margin-bottom: 1rem;
  }
`;

const ContentSection = styled.section`
  margin-bottom: 2.5rem;

  p {
    font-size: 16px;
    line-height: 1.8;
    color: #3a3a3a;
    font-family: Montserrat, sans-serif;
    margin-bottom: 1rem;
  }
`;

const SectionHeading = styled.h2`
  font-size: 1.4rem;
  font-weight: 700;
  color: #1a1a2e;
  margin-bottom: 1rem;
  font-family: Montserrat, sans-serif;

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const BulletList = styled.ul`
  padding-left: 1.4rem;
  margin-bottom: 1rem;

  li {
    font-size: 16px;
    line-height: 1.8;
    color: #3a3a3a;
    font-family: Montserrat, sans-serif;
    margin-bottom: 0.6rem;
  }
`;

const FaqList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  overflow: hidden;
`;

const FaqRow = styled.div`
  border-bottom: 1px solid #e5e5e5;

  &:last-child {
    border-bottom: none;
  }
`;

const FaqQuestion = styled.button`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #ffffff;
  border: none;
  padding: 1rem 1.25rem;
  text-align: left;
  cursor: pointer;
  font-size: 15px;
  font-weight: 600;
  color: #1a1a2e;
  font-family: Montserrat, sans-serif;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: #f7f9ff;
  }
`;

const FaqIcon = styled.span`
  font-size: 20px;
  font-weight: 400;
  color: #00419d;
  flex-shrink: 0;
  margin-left: 1rem;
`;

const FaqAnswer = styled.div`
  padding: 0 1.25rem 1rem;
  font-size: 15px;
  line-height: 1.75;
  color: #555555;
  font-family: Montserrat, sans-serif;
  background: #fafafa;
`;
